<template>
  <div class="legal-page">
    <MainNavbar />

    <div class="legal-hero">
      <div class="hero-content">
        <h1>Facility Ownership Terms &amp; Conditions</h1>
        <p>Last Updated: April 18, 2026</p>
        <p class="hero-subline">
          For pitch owners registering sports facilities on IPromiseAfrica (IPA) for discovery and bookings.
        </p>
      </div>
      <LegalDocumentSeal />
    </div>

    <div class="legal-container">
      <div class="legal-content">
        <div class="legal-section">
          <h2>IPA Facility &amp; Pitch Owner Agreement</h2>
          <p class="legal-intro">
            These Facility Ownership Terms and Conditions (&ldquo;Facility Terms&rdquo;) govern your registration
            and listing of a sports facility or pitch (&ldquo;Facility&rdquo;) on the IPromiseAfrica platform
            (&ldquo;IPA&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;). By submitting a facility registration, you
            confirm that you are authorized to represent the Facility and that you accept these Facility Terms in
            addition to the general
            <router-link class="inline-legal-link" :to="{ name: 'TermsOfUse' }">Terms of Use</router-link>
            and
            <router-link class="inline-legal-link" :to="{ name: 'PrivacyPolicy' }">Privacy Policy</router-link>
            where they apply to your use of the site and your account data.
          </p>
          <p class="legal-notice">
            IPA provides software and visibility for your Facility; operational control, safety, compliance with
            local law, and the accuracy of your listing remain your responsibility unless expressly stated otherwise
            in a separate written agreement with IpromiseAfrica Inc.
          </p>
        </div>

        <div class="legal-section">
          <div class="dropdown-section">
            <button type="button" class="dropdown-header" @click="toggleDropdown('eligibility')">
              <span>Eligibility, authority &amp; registration</span>
              <i :class="dropdownStates.eligibility ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"></i>
            </button>
            <div class="dropdown-content" v-show="dropdownStates.eligibility">
              <p>
                You must be a logged-in IPA customer with authority to list the Facility (e.g. owner, lessee with
                listing rights, or authorized manager). Information you provide &mdash; including location pin,
                photos, amenities, rates, and contact routing &mdash; must be truthful and kept current.
              </p>
              <p>
                You consent to IPA displaying profile-linked contact details you have already provided on IPA to
                other users for booking-related communication, as described at registration.
              </p>
            </div>
          </div>

          <div class="dropdown-section">
            <button type="button" class="dropdown-header" @click="toggleDropdown('listing')">
              <span>Listing content, imagery &amp; conduct</span>
              <i :class="dropdownStates.listing ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"></i>
            </button>
            <div class="dropdown-content" v-show="dropdownStates.listing">
              <p>
                You are responsible for all text, images, and amenity selections associated with your Facility. You
                must not upload misleading photos, impersonate another venue, or infringe third-party rights. IPA
                may remove or restrict content that violates applicable law, these Facility Terms, or community
                standards.
              </p>
              <ul>
                <li>Maintain safe and lawful use of the Facility as advertised.</li>
                <li>Honor confirmed bookings according to your stated rules and applicable law.</li>
                <li>Respond professionally to players, teams, and IPA support inquiries related to your listing.</li>
              </ul>
            </div>
          </div>

          <div class="dropdown-section">
            <button type="button" class="dropdown-header" @click="toggleDropdown('liability')">
              <span>Liability, disclaimers &amp; indemnity</span>
              <i :class="dropdownStates.liability ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"></i>
            </button>
            <div class="dropdown-content" v-show="dropdownStates.liability">
              <p>
                To the fullest extent permitted by law, IPA is not liable for injuries, losses, or disputes arising
                at the Facility or from off-platform arrangements between you and guests, except where mandatory
                law provides otherwise. You agree to indemnify and hold harmless IpromiseAfrica Inc. and its
                affiliates from claims arising from your Facility operations, listing content, or breach of these
                Facility Terms, subject to any limits under applicable law.
              </p>
            </div>
          </div>

          <div class="dropdown-section">
            <button type="button" class="dropdown-header" @click="toggleDropdown('changes')">
              <span>Changes, suspension &amp; termination</span>
              <i :class="dropdownStates.changes ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"></i>
            </button>
            <div class="dropdown-content" v-show="dropdownStates.changes">
              <p>
                IPA may update these Facility Terms from time to time. The &ldquo;Last Updated&rdquo; date at the top
                will change; continued use of pitch-owner features after changes constitutes acceptance where
                permitted by law. IPA may suspend or remove listings or access that present risk, fraud, or legal
                exposure, or that violate platform rules.
              </p>
            </div>
          </div>
        </div>

        <div class="legal-footer-notice">
          <p>Copyright &copy; 2026 IpromiseAfrica Inc. All rights reserved.</p>
          <p>IpromiseAfrica Inc., Nairobi, Republic of Kenya.</p>
        </div>

        <div class="legal-actions">
          <router-link class="back-btn" :to="{ name: 'FacilityOwnerRegistration' }">
            <i class="fa-solid fa-arrow-left"></i>
            Back to pitch registration
          </router-link>
          <router-link class="back-btn secondary-link" :to="{ name: 'MainPage' }">
            <i class="fa-solid fa-house"></i>
            IPA Store home
          </router-link>
        </div>
      </div>
    </div>

    <MainFooter />
  </div>
</template>

<script>
import MainNavbar from "../../components/main/headernfooter/header.vue";
import MainFooter from "../../components/main/headernfooter/Footer.vue";
import LegalDocumentSeal from "../../components/legal/LegalDocumentSeal.vue";

export default {
  name: "FacilityOwnershipTerms",
  components: {
    MainNavbar,
    MainFooter,
    LegalDocumentSeal,
  },
  data() {
    return {
      dropdownStates: {
        eligibility: true,
        listing: false,
        liability: false,
        changes: false,
      },
    };
  },
  methods: {
    toggleDropdown(section) {
      this.dropdownStates[section] = !this.dropdownStates[section];
    },
  },
  mounted() {
    window.scrollTo(0, 0);
  },
};
</script>

<style scoped>
.legal-page {
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  font-family: "Poppins", sans-serif;
  background: var(--background-color1);
  color: var(--text-color1);
  overflow-x: clip;
}

.legal-hero {
  background: linear-gradient(
    145deg,
    var(--background-color2) 0%,
    color-mix(in srgb, var(--background-color1) 88%, var(--background-color2)) 100%
  );
  color: var(--text-color1);
  padding: clamp(3rem, 8vw, 5rem) clamp(1rem, 4vw, 1.5rem);
  text-align: center;
  margin-top: max(70px, calc(env(safe-area-inset-top, 0px) + 64px));
  position: relative;
  overflow: hidden;
  border-bottom: 1px solid color-mix(in srgb, var(--border-color-hover) 22%, transparent);
}

.legal-hero .hero-content {
  position: relative;
  z-index: 2;
}

.hero-content h1 {
  font-size: clamp(1.85rem, 4.5vw, 3rem);
  font-weight: 800;
  margin-bottom: 10px;
  color: var(--text-color1);
  letter-spacing: 0.02em;
}

.hero-content p {
  color: var(--text-color2);
  font-size: clamp(0.95rem, 2vw, 1.1rem);
}

.hero-subline {
  max-width: 640px;
  margin: 12px auto 0;
  font-size: clamp(0.88rem, 1.8vw, 0.98rem) !important;
  line-height: 1.5;
  color: var(--text-color2);
}

.legal-container {
  flex: 1 0 auto;
  width: 100%;
  max-width: min(1200px, 100% - 2rem);
  margin: clamp(1.5rem, 4vw, 2.5rem) auto;
  padding: 0 clamp(0.85rem, 3vw, 1.25rem);
  box-sizing: border-box;
}

.legal-content {
  background: var(--background-color1);
  border: 1px solid color-mix(in srgb, var(--border-color-hover) 20%, transparent);
  border-radius: clamp(10px, 2vw, 14px);
  box-shadow: 0 8px 28px color-mix(in srgb, var(--border-color-hover) 6%, transparent);
  padding: clamp(1.25rem, 4vw, 2.5rem);
}

.legal-section {
  margin-bottom: clamp(1.25rem, 3vw, 1.75rem);
}

.legal-section h2 {
  color: var(--text-color1);
  font-size: clamp(1.35rem, 3vw, 1.8rem);
  font-weight: 700;
  margin-bottom: 1rem;
  padding-bottom: 0.65rem;
  border-bottom: 2px solid var(--border-color-hover);
}

.legal-intro {
  font-size: clamp(0.95rem, 2vw, 1.1rem);
  line-height: 1.7;
  color: var(--text-color2);
  margin-bottom: 1.25rem;
}

.legal-notice {
  background: color-mix(in srgb, var(--border-color-hover) 10%, var(--background-color2));
  border-left: 4px solid var(--border-color-hover);
  padding: clamp(0.85rem, 2vw, 1rem);
  font-size: clamp(0.88rem, 1.8vw, 0.95rem);
  line-height: 1.6;
  color: var(--text-color2);
  border-radius: 0 8px 8px 0;
}

.inline-legal-link {
  color: var(--border-color-hover);
  font-weight: 600;
  text-decoration: none;
}

.inline-legal-link:hover {
  text-decoration: underline;
}

.dropdown-section {
  border: 1px solid color-mix(in srgb, var(--border-color-hover) 18%, transparent);
  border-radius: 10px;
  margin-bottom: 0.85rem;
  overflow: hidden;
  transition: border-color 0.25s ease;
}

.dropdown-section:hover {
  border-color: color-mix(in srgb, var(--border-color-hover) 45%, transparent);
}

.dropdown-header {
  width: 100%;
  padding: clamp(0.85rem, 2.5vw, 1.25rem);
  background: var(--background-color2);
  border: none;
  text-align: left;
  font-size: clamp(0.95rem, 2vw, 1.1rem);
  font-weight: 600;
  color: var(--text-color1);
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  transition: background 0.25s ease;
}

.dropdown-header:hover {
  background: color-mix(in srgb, var(--border-color-hover) 8%, var(--background-color2));
}

.dropdown-header i {
  color: var(--border-color-hover);
  font-size: 0.9rem;
  flex-shrink: 0;
}

.dropdown-content {
  padding: clamp(1rem, 3vw, 1.5rem);
  background: color-mix(in srgb, var(--background-color2) 55%, var(--background-color1));
  border-top: 1px solid color-mix(in srgb, var(--border-color-hover) 14%, transparent);
  animation: slideDown 0.3s ease;
}

.dropdown-content p,
.dropdown-content li {
  line-height: 1.65;
  color: var(--text-color2);
}

.dropdown-content ul {
  padding-left: 1.25rem;
  margin: 0.85rem 0;
}

.dropdown-content li {
  margin-bottom: 0.65rem;
}

.dropdown-content strong {
  color: var(--text-color1);
}

.legal-footer-notice {
  margin-top: clamp(1.5rem, 4vw, 2.5rem);
  padding-top: 1.25rem;
  border-top: 1px solid color-mix(in srgb, var(--text-color2) 12%, transparent);
  text-align: center;
  color: var(--text-color2);
  font-size: clamp(0.82rem, 1.8vw, 0.9rem);
}

.legal-footer-notice p {
  margin: 0.35rem 0;
}

.legal-actions {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: clamp(0.75rem, 2vw, 1.25rem);
  margin-top: clamp(1.5rem, 4vw, 2.5rem);
  padding-top: clamp(1.25rem, 3vw, 1.75rem);
  border-top: 1px solid color-mix(in srgb, var(--text-color2) 12%, transparent);
}

.back-btn {
  background: var(--background-color2);
  color: var(--text-color1);
  border: 1px solid color-mix(in srgb, var(--border-color-hover) 25%, transparent);
  padding: clamp(0.65rem, 2vw, 0.75rem) clamp(1.25rem, 3vw, 1.75rem);
  border-radius: 32px;
  font-weight: 600;
  font-size: clamp(0.88rem, 1.8vw, 0.95rem);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 44px;
  transition: transform 0.2s ease, border-color 0.2s ease, background 0.2s ease;
}

.back-btn:hover {
  background: color-mix(in srgb, var(--border-color-hover) 10%, var(--background-color2));
  border-color: var(--border-color-hover);
  color: var(--text-color1);
}

.back-btn.secondary-link {
  border-color: color-mix(in srgb, var(--text-color2) 20%, transparent);
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 768px) {
  .legal-content {
    padding: clamp(1rem, 3.5vw, 1.5rem);
  }

  .dropdown-header {
    font-size: 0.95rem;
  }

  .legal-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .back-btn {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .dropdown-content {
    padding: 1rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .dropdown-content {
    animation: none;
  }

  .back-btn:hover {
    transform: none;
  }
}
</style>
